/**
 * Admin LeadsPage
 *
 * Every registered visitor is a lead. Lists all accounts with their contact
 * details (email, phone) and category (host / guest), with search, segment
 * filters and a full CSV export for outreach.
 */

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { AdminLayout } from '../layout';
import {
  useLeads,
  EMPTY_LEADS_FILTER,
  type LeadsFilter,
  type LeadCategory,
  type LeadSource,
} from './hooks';
import { downloadLeadsCsv } from './leadsCsv';

const CATEGORY_STYLES: Record<LeadCategory, string> = {
  host: 'bg-primary-100 text-primary-700',
  guest: 'bg-blue-100 text-blue-700',
  admin: 'bg-purple-100 text-purple-700',
};

const formatDate = (d?: Date): string =>
  d && !isNaN(d.getTime()) && d.getTime() > 0
    ? d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
    : '—';

const StatCard: React.FC<{ label: string; value: number }> = ({ label, value }) => (
  <div className="bg-white rounded-xl border border-gray-200 p-4">
    <p className="text-xs uppercase tracking-wide text-gray-500">{label}</p>
    <p className="text-2xl font-semibold text-gray-900 mt-1">{value.toLocaleString()}</p>
  </div>
);

export const LeadsPage: React.FC = () => {
  const [filter, setFilter] = useState<LeadsFilter>(EMPTY_LEADS_FILTER);
  const { leads, stats, isLoading, error } = useLeads(filter);

  const handleExport = () => {
    const stamp = new Date().toISOString().split('T')[0];
    downloadLeadsCsv(leads, `darak-leads-${stamp}.csv`);
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-xl font-semibold text-gray-900 mb-1">Leads</h1>
            <p className="text-sm text-gray-500">
              Every registered visitor, with contact details and whether they
              signed up as a host or a guest.
            </p>
          </div>
          <button
            onClick={handleExport}
            disabled={isLoading || leads.length === 0}
            className="shrink-0 px-4 py-2 bg-primary-500 text-white font-medium rounded-lg hover:bg-primary-600 disabled:opacity-50"
          >
            Export CSV ({leads.length})
          </button>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard label="Total leads" value={stats.total} />
          <StatCard label="Hosts" value={stats.hosts} />
          <StatCard label="Guests" value={stats.guests} />
          <StatCard label="With phone" value={stats.withPhone} />
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl border border-gray-200 p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="lg:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
            <input
              type="text"
              value={filter.search}
              onChange={(e) => setFilter({ ...filter, search: e.target.value })}
              placeholder="Name, email or phone"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
            <select
              value={filter.category}
              onChange={(e) =>
                setFilter({ ...filter, category: e.target.value as LeadCategory | '' })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="">All categories</option>
              <option value="host">Hosts</option>
              <option value="guest">Guests</option>
              <option value="admin">Admins</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Source</label>
            <select
              value={filter.source}
              onChange={(e) =>
                setFilter({ ...filter, source: e.target.value as LeadSource | '' })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="">All sources</option>
              <option value="app">App signup</option>
              <option value="legacy">Legacy import</option>
            </select>
          </div>
          <label className="flex items-center gap-2 cursor-pointer sm:col-span-2">
            <input
              type="checkbox"
              checked={filter.withPhoneOnly}
              onChange={(e) => setFilter({ ...filter, withPhoneOnly: e.target.checked })}
              className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            <span className="text-sm text-gray-700">Only leads with a phone number</span>
          </label>
        </div>

        {/* Table */}
        {isLoading ? (
          <div className="p-8 text-center text-gray-500">Loading leads…</div>
        ) : error ? (
          <div className="bg-white rounded-xl border border-red-200 p-8 text-center text-red-600">
            {error}
          </div>
        ) : leads.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-200 p-8 text-center text-gray-500">
            No leads match these filters.
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200 text-xs font-semibold uppercase tracking-wide text-gray-500">
                  <th className="px-4 py-2.5 text-left">Name</th>
                  <th className="px-4 py-2.5 text-left">Email</th>
                  <th className="px-4 py-2.5 text-left">Phone</th>
                  <th className="px-4 py-2.5 text-left">Category</th>
                  <th className="px-4 py-2.5 text-left">Source</th>
                  <th className="px-4 py-2.5 text-left">Registered</th>
                </tr>
              </thead>
              <tbody>
                {leads.map((l) => (
                  <tr key={l.id} className="border-b border-gray-100 last:border-0">
                    <td className="px-4 py-3">
                      <Link
                        to={`/admin/users/${l.id}`}
                        className="font-medium text-gray-900 hover:text-primary-600"
                      >
                        {l.name || '—'}
                      </Link>
                      {l.isSuspended && (
                        <span className="ml-2 text-xs px-2 py-0.5 rounded bg-red-100 text-red-700">
                          Suspended
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {l.email ? (
                        <a href={`mailto:${l.email}`} className="text-primary-600 hover:underline">
                          {l.email}
                        </a>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {l.phone ? (
                        <a href={`tel:${l.phone}`} className="text-primary-600 hover:underline">
                          {l.phone}
                        </a>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`text-xs font-medium px-2 py-1 rounded capitalize ${
                          CATEGORY_STYLES[l.category] || 'bg-gray-100 text-gray-600'
                        }`}
                      >
                        {l.category}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                      {l.source === 'legacy' ? 'Legacy import' : 'App signup'}
                    </td>
                    <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                      {formatDate(l.createdAt)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default LeadsPage;
